// public/js/modules/ui/spinner.js
// Central loading overlay — turbine SVG for all full-screen loading states.
// The airplane (AeroButton) stays exclusively in the logout button.
// Usage: window.showSpinner(message?) / window.hideSpinner()
// Legacy aliases (backward-compatible): showPlaneSpinner, hidePlaneSpinner,
//   showCenteredPlaneSpinner, hideCenteredPlaneSpinner

const OVERLAY_ID = 'superb-spinner-overlay';
let   _stylesInjected = false;

// ─── keyframe injection (once) ───────────────────────────────────────────────

function injectStyles() {
  if (_stylesInjected) return;
  _stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes _sb-fadein {
      from { opacity: 0; transform: translateY(6px) scale(0.97); }
      to   { opacity: 1; transform: translateY(0)   scale(1);    }
    }
    @keyframes _sb-turbine {
      to { transform: rotate(360deg); }
    }
    #${OVERLAY_ID} > .sb-card {
      animation: _sb-fadein 0.22s cubic-bezier(0.22, 1, 0.36, 1) both;
    }
    .sb-turbine-blades {
      transform-box: fill-box;
      transform-origin: center;
      animation: _sb-turbine 1.4s linear infinite;
    }
  `;
  document.head.appendChild(style);
}

// ─── turbine SVG (inline — no external request) ──────────────────────────────
// Outer nacelle ring: static, rgba white.
// Inner group: rotates via <animateTransform>, blue-300 stroke + blue glow.

function buildTurbineSVG(size = 96, opts = {}) {
  // ring  = faint outer nacelle ring (overlay only; invisible/no use when tiny)
  // glow  = blue drop-shadow (looks good on the dark overlay, bad inline on white)
  // color/strokeWidth = blade styling (darker + thicker reads better when small)
  const { ring = true, glow = true, color = '#93c5fd', strokeWidth = 2 } = opts;
  const glowStyle = glow
    ? 'filter:drop-shadow(0 0 16px rgba(96,165,250,0.55)) drop-shadow(0 0 4px rgba(96,165,250,0.9))'
    : 'display:block';
  const ringEl = ring
    ? '<circle cx="50" cy="50" r="45.5" fill="none" stroke="rgba(255,255,255,0.18)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
    : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"
       width="${size}" height="${size}" aria-hidden="true"
       style="${glowStyle}">
  ${ringEl}
  <g class="sb-turbine-blades" fill="none" stroke="${color}" stroke-width="${strokeWidth}"
     stroke-linecap="round" stroke-linejoin="round">
    <path d="M50 35 Q44.22 20.06 31.59 12.25"/>
    <path d="M54.64 35.73 Q53.76 19.74 44.15 8.41"/>
    <path d="M58.82 37.86 Q62.93 22.38 57.29 8.64"/>
    <path d="M62.14 41.18 Q70.83 27.73 69.72 12.92"/>
    <path d="M64.27 45.36 Q76.69 35.25 80.21 20.82"/>
    <path d="M65 50 Q79.94 44.22 87.75 31.59"/>
    <path d="M64.27 54.64 Q80.26 53.76 91.59 44.15"/>
    <path d="M62.14 58.82 Q77.62 62.93 91.36 57.29"/>
    <path d="M58.82 62.14 Q72.27 70.83 87.08 69.72"/>
    <path d="M54.64 64.27 Q64.75 76.69 79.18 80.21"/>
    <path d="M50 65 Q55.78 79.94 68.41 87.75"/>
    <path d="M45.36 64.27 Q46.24 80.26 55.85 91.59"/>
    <path d="M41.18 62.14 Q37.07 77.62 42.71 91.36"/>
    <path d="M37.86 58.82 Q29.17 72.27 30.28 87.08"/>
    <path d="M35.73 54.64 Q23.31 64.75 19.79 79.18"/>
    <path d="M35 50 Q20.06 55.78 12.25 68.41"/>
    <path d="M35.73 45.36 Q19.74 46.24 8.41 55.85"/>
    <path d="M37.86 41.18 Q22.38 37.07 8.64 42.71"/>
    <path d="M41.18 37.86 Q27.73 29.17 12.92 30.28"/>
    <path d="M45.36 35.73 Q35.25 23.31 20.82 19.79"/>
    <circle cx="50" cy="50" r="13"/>
    <path d="M50 48.9 L49.87 48.87 L49.74 48.85 L49.6 48.85 L49.45 48.87 L49.3 48.9 L49.16 48.95 L49.01 49.02 L48.87 49.11 L48.73 49.22 L48.61 49.34 L48.49 49.49 L48.39 49.65 L48.3 49.82 L48.24 50.02 L48.19 50.22 L48.17 50.44 L48.17 50.67 L48.19 50.9 L48.25 51.13 L48.33 51.37 L48.45 51.6 L48.59 51.83 L48.76 52.04 L48.97 52.24 L49.2 52.42 L49.46 52.59 L49.75 52.72 L50.06 52.82 L50.39 52.9 L50.73 52.93 L51.1 52.93 L51.47 52.88 L51.84 52.79 L52.22 52.65 L52.59 52.47 L52.95 52.23 L53.29 51.95 L53.61 51.62 L53.9 51.25 L54.15 50.83 L54.36 50.36 L54.53 49.87 L54.64 49.34 L54.69 48.78 L54.67 48.2 L54.59 47.6 L54.44 47 L54.21 46.4 L53.91 45.81 L53.53 45.24 L53.07 44.7 L52.54 44.19 L51.93 43.73 L51.26 43.33 L50.52 43 L49.72 42.75 L48.86 42.58 L47.97 42.51 L47.04 42.54 L46.09 42.68 L45.12 42.93 L44.17 43.3 L43.22 43.8 L42.31 44.42"/>
    <path d="M50 51.1 L50.13 51.13 L50.26 51.15 L50.4 51.15 L50.55 51.13 L50.7 51.1 L50.84 51.05 L50.99 50.98 L51.13 50.89 L51.27 50.78 L51.39 50.66 L51.51 50.51 L51.61 50.35 L51.7 50.18 L51.76 49.98 L51.81 49.78 L51.83 49.56 L51.83 49.33 L51.81 49.1 L51.75 48.87 L51.67 48.63 L51.55 48.4 L51.41 48.17 L51.24 47.96 L51.03 47.76 L50.8 47.58 L50.54 47.41 L50.25 47.28 L49.94 47.18 L49.61 47.1 L49.27 47.07 L48.9 47.07 L48.53 47.12 L48.16 47.21 L47.78 47.35 L47.41 47.53 L47.05 47.77 L46.71 48.05 L46.39 48.38 L46.1 48.75 L45.85 49.17 L45.64 49.64 L45.47 50.13 L45.36 50.66 L45.31 51.22 L45.33 51.8 L45.41 52.4 L45.56 53 L45.79 53.6 L46.09 54.19 L46.47 54.76 L46.93 55.3 L47.46 55.81 L48.07 56.27 L48.74 56.67 L49.48 57 L50.28 57.25 L51.14 57.42 L52.03 57.49 L52.96 57.46 L53.91 57.32 L54.88 57.07 L55.83 56.7 L56.78 56.2 L57.69 55.58"/>
  </g>
</svg>`;
}

// ─── small inline spinner ─────────────────────────────────────────────────────
// Same turbine as the full-screen loader, but tiny — for non-blocking inline
// loading states (status lines, buttons). Ensures the rotation keyframes exist.
export function spinnerSVG(size = 16, opts = {}) {
  injectStyles();
  // No ring, no glow, darker + thicker blades → clearly visible at small sizes
  // on light backgrounds.
  return buildTurbineSVG(size, { ring: false, glow: false, color: '#2563eb', strokeWidth: 4, ...opts });
}

// ─── public API ───────────────────────────────────────────────────────────────

export function showSpinner(message = 'Loading...') {
  hideSpinner(); // ensure no duplicate
  injectStyles();

  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:9999',
    'display:flex', 'align-items:center', 'justify-content:center',
    'background:rgba(8,14,28,0.88)',
    'backdrop-filter:blur(12px)', '-webkit-backdrop-filter:blur(12px)',
  ].join(';');

  // Dismiss on Escape
  const escHandler = (e) => { if (e.key === 'Escape') hideSpinner(); };
  document.addEventListener('keydown', escHandler);
  overlay._escHandler = escHandler;

  overlay.innerHTML = `
    <div class="sb-card" style="
      display:flex; flex-direction:column; align-items:center; gap:28px;
      padding:44px 56px;
      background:rgba(15,23,42,0.72);
      border:1px solid rgba(255,255,255,0.1);
      border-radius:24px;
      box-shadow:0 40px 100px rgba(0,0,0,0.6),
                 0 0 0 1px rgba(255,255,255,0.04),
                 inset 0 1px 0 rgba(255,255,255,0.08);
      min-width:220px; text-align:center;">
      ${buildTurbineSVG(96)}
      <span style="
        color:rgba(255,255,255,0.88);
        font-size:0.95rem; font-weight:500;
        letter-spacing:0.05em; text-transform:uppercase;
        font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;">
        ${message}
      </span>
    </div>`;

  document.body.appendChild(overlay);
}

export function hideSpinner() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (!overlay) return;
  if (overlay._escHandler) document.removeEventListener('keydown', overlay._escHandler);
  overlay.remove();
}

// ─── legacy aliases (all existing call-sites keep working unchanged) ──────────

export function showPlaneSpinner(_container, message = 'Loading...') {
  showSpinner(message);
}
export function hidePlaneSpinner() { hideSpinner(); }
export function showCenteredPlaneSpinner(message = 'Loading...') { showSpinner(message); }
export function hideCenteredPlaneSpinner() { hideSpinner(); }

// ─── global registration (window.showSpinner etc.) ───────────────────────────

window.showSpinner              = showSpinner;
window.hideSpinner              = hideSpinner;
window.showPlaneSpinner         = showPlaneSpinner;
window.hidePlaneSpinner         = hidePlaneSpinner;
window.showCenteredPlaneSpinner = showCenteredPlaneSpinner;
window.hideCenteredPlaneSpinner = hideCenteredPlaneSpinner;
